/**
 * Service Worker Registration pour CareWashGo
 * Version 2.0 - Gestion avancée des mises à jour
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Callbacks globaux pour les composants UI
let updateCallback = null;
let successCallback = null;

export function onUpdateAvailable(callback) {
  updateCallback = callback;
}

export function onCacheSuccess(callback) {
  successCallback = callback;
}

export function register(config) {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // En localhost, vérifier que le SW existe
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then((registration) => {
          setupUpdateCheck(registration);
        });
      } else {
        // En production, enregistrer directement
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      
      // Vérifier les mises à jour périodiquement
      setupUpdateCheck(registration);
      
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Nouveau contenu disponible
              
              // Notifier via callback
              if (updateCallback) {
                updateCallback(registration);
              }
              
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Contenu mis en cache pour la première fois
              
              if (successCallback) {
                successCallback(registration);
              }
              
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Erreur lors de l\'enregistrement:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Vérifier que le service worker existe
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // Service Worker non trouvé, recharger la page
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service Worker trouvé, l'enregistrer
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
    });
}

/**
 * Configurer la vérification automatique des mises à jour
 */
function setupUpdateCheck(registration) {
  // Vérifier les mises à jour toutes les heures
  setInterval(() => {
    registration.update().catch((err) => {
    });
  }, 60 * 60 * 1000); // 1 heure
  
  // Vérifier aussi quand l'app reprend le focus
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      registration.update().catch(() => {});
    }
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('[SW] Erreur de désenregistrement:', error);
      });
  }
}

/**
 * Force la mise à jour du Service Worker
 */
export function forceUpdate() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        if (registration.waiting) {
          // Envoyer le message SKIP_WAITING au SW en attente
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        return registration.update();
      })
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error('[SW] Erreur de mise à jour forcée:', error);
      });
  }
}

/**
 * Enregistrer une synchronisation en arrière-plan
 */
export function registerSync(tag) {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready
      .then((registration) => {
        return registration.sync.register(tag);
      })
      .then(() => {
      })
      .catch((error) => {
        console.error('[SW] Erreur de synchronisation:', error);
      });
  }
}

/**
 * Envoyer un message au Service Worker
 */
export function sendMessageToSW(message) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
}

/**
 * Vider tous les caches
 */
export async function clearAllCaches() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
  }
}

/**
 * Obtenir la taille totale des caches
 */
export async function getCacheSize() {
  if ('caches' in window && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return {
      used: estimate.usage || 0,
      quota: estimate.quota || 0,
      usedMB: ((estimate.usage || 0) / (1024 * 1024)).toFixed(2),
      quotaMB: ((estimate.quota || 0) / (1024 * 1024)).toFixed(2)
    };
  }
  return null;
}
